import align from "@assets/redesign/icons/align.svg";
import rotate from "@assets/redesign/icons/rotate.svg";
import Icon from "@components/global/new/Icon/Icon";
import useEscape from "@mobx/hooks/useEscape";
import mainLocationsStore from "@mobx/store/locations";
import { makeNotifs } from "@utils/makeNotifs";
import { pub } from "@utils/pubsub";
import { useToasts } from "@utils/hooks/useToasts";
import cn from "classnames";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import Move3 from "../Move3";
import RackReorderGroup3 from "../RackReorderGroup3";
import RackTableHeader3 from "../RackTableHeader3";
import { dropRackItemToUnallocated } from "../UnallocatedMiners3/dropRackItem";
import {
  isPointOutsideElement,
  isPointOutsideElementTree,
  Point,
  RACK_ITEM_DRAG_END_EVENT,
  RackItemDragEndDetail
} from "./dropOutside";
import styles from "./RackTable.module.scss";
import useDrag from "./useDrag";

export interface IRackTable3Props {
  readonly className?: string;
}

const useControl = (props: IRackTable3Props) => {
  const locationStore = mainLocationsStore.activeLocation;
  const buildingStore = locationStore?.activeBuilding;
  const dragHelper = useDrag();
  const rackDataWrapper = useRef<HTMLDivElement | null>(null);
  const lastDragPoint = useRef<Point>();
  const { error } = useToasts();
  const { errors } = makeNotifs();

  useEffect(() => {
    const cancelActiveRackItem = () => {
      buildingStore?.setActiveRackItemId();
      buildingStore?.removeActiveIdentifiers();
    };

    document.addEventListener("click", cancelActiveRackItem);

    return () => document.removeEventListener("click", cancelActiveRackItem);
  }, []);

  useEscape(() => buildingStore?.setActiveRackItemId(undefined));

  useEffect(() => {
    const onDragOver = (ev: DragEvent) => {
      lastDragPoint.current = { x: ev.clientX, y: ev.clientY };
    };

    document.addEventListener("dragover", onDragOver);
    return () => document.removeEventListener("dragover", onDragOver);
  }, []);

  useEffect(() => {
    const onRackItemDragEnd = async (ev: Event) => {
      try {
        const { item, point } = (ev as CustomEvent<RackItemDragEndDetail>).detail;
        const finalPoint = point ?? lastDragPoint.current;
        const outsideByRect = isPointOutsideElement(rackDataWrapper.current, finalPoint);
        const outsideByTree = isPointOutsideElementTree(rackDataWrapper.current, finalPoint);
        const outside = outsideByRect || outsideByTree;

        if (!window.TRANSFER_RACK_ITEM) return;
        if (!outside) return;

        await dropRackItemToUnallocated({
          buildingStore,
          onMergedError: () => error(errors.locations__unnalocated_unmerge),
          rackItemData: item,
        });
      } finally {
        lastDragPoint.current = undefined;
      }
    };

    window.addEventListener(RACK_ITEM_DRAG_END_EVENT, onRackItemDragEnd);
    return () => {
      window.removeEventListener(RACK_ITEM_DRAG_END_EVENT, onRackItemDragEnd);
    };
  }, [buildingStore, error, errors.locations__unnalocated_unmerge]);

  return {
    dragHelper,
    validRacks: !!buildingStore?.isMinersStoreValid,
    buildingStore,
    rackDataWrapper,

    toggleFlipped: () => buildingStore?.toggleFlipped(),
  };
};

const RackTable3 = observer((props: IRackTable3Props) => {
  const { className } = props;
  const { formatMessage } = useIntl();
  const ctrl = useControl(props);
  const [isDisabled, setIsDisabled] = useState(0);

  if (!ctrl.buildingStore) return null;
  return (
    <div
      className={cn(styles.wrapper, className)}
      onDragEnter={ctrl.dragHelper.onAvailableDraging}
    >
      {ctrl.dragHelper.showDraggingOverlay && (
        <div
          className={styles.availableDragging}
          onDragLeave={ctrl.dragHelper.onOverlayLeave}
          onDrop={ctrl.dragHelper.onDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          + {formatMessage({ id: "account.add" })}
        </div>
      )}
      <div className={styles.rack_header}>
        <div
          className={styles.rotate}
          title={formatMessage({ id: "location.rackTable.rotate.title" })}
          onClick={ctrl.toggleFlipped}
        >
          <div className={styles.rotate_btn}>
            <Icon src={rotate} noHover />
          </div>
          <p>{formatMessage({ id: "location.rackTable.rotate" })}</p>
        </div>
        <div
          className={styles.rotate}
          title={formatMessage({ id: "location.rackTable.align.title" })}
          onClick={() => pub("location-align-racks", null)}
        >
          <div className={styles.rotate_btn}>
            <Icon src={align} noHover />
          </div>
          <p>{formatMessage({ id: "location.rackTable.align" })}</p>
        </div>
      </div>
      <div className={styles.rack_data_wrapper} ref={ctrl.rackDataWrapper}>
        <div className={styles.miners_wrapper}>
          <RackTableHeader3 />
          <Move3 setIsDisabled={setIsDisabled} />
          <RackReorderGroup3
            lock={ctrl.buildingStore.isLocked}
            disabled={isDisabled > 0}
            validRacks={ctrl.validRacks}
            onDragOver={ctrl.dragHelper.onDragOverRackTable}
            onDragEnter={ctrl.dragHelper.onDragOverRackTable}
            onDragLeave={ctrl.dragHelper.onRackTemplateLeave}
            onNewRackSaved={ctrl.dragHelper.onNewRackSaved}
          />
        </div>
      </div>
    </div>
  );
});

RackTable3.displayName = "RackTable3";

export default RackTable3;
